import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {hashObject} from "@cm/utils"
import {ImageNode} from "#template/nodes/image/image-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ImageLikeNode, NumberLike} from "#template/nodes/types"
import {imageLike} from "#template/nodes/image/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import {generateImageNodeForInMemoryImageData} from "#template/nodes/image/in-memory-image"
import type {BuilderOutlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import {HeightToNormal, materialConnection, Mapping, MaterialConnection, TexImage, UvMap} from "@cm/material-nodes"
import type {ImageGenerator, ImageNodeGeneratorData} from "@cm/material-nodes"
import type {BakedTextureData} from "#template/runtime-graph/nodes/bake-texture"
import {BakeTexture} from "#template/runtime-graph/nodes/bake-texture"
import type {HashedMaterialNodeWithColorOutput} from "#template/runtime-graph/type-descriptors"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {visitNone} from "#template/template-node/visit-helpers"
const TD = TypeDescriptors

const bakedTextureDataDesc = TD.Identity<BakedTextureData>("contentHash")

const getImageGeneratorDataForWidthAndHeight = (width: number, height: number): ImageNodeGeneratorData => ({
    uv: materialConnection(
        new Mapping({
            parameters: {
                inputs: {
                    vector: materialConnection(
                        new UvMap({
                            parameters: {
                                inputs: {},
                                settings: {
                                    uvMapIndex: 0,
                                },
                            },
                        }),
                        "uv",
                    ),
                },
                settings: {
                    vectorType: "TEXTURE",
                    scale: {x: width, y: height, z: 1},
                },
            },
        }),
        "vector",
    ),
    extension: "REPEAT",
    interpolation: "Closest",
    projection: "FLAT",
    disableProgressiveLoading: true,
    forceOriginalResolution: true,
})

export type ImageDisplacementToNormalParameters = {
    name: string
    displacement: ImageLikeNode | null
    displacementMax: NumberLike
    displacementMin: NumberLike
    displacementNormalStrength: NumberLike
    displacementNormalSmoothness: NumberLike
    resolution: NumberLike
    width: NumberLike
    height: NumberLike
}

const imageDisplacementToNormalDefinition: TemplateNodeDefinition<ImageDisplacementToNormalParameters> = {
    nodeClass: "ImageDisplacementToNormal",
    validation: {
        parameters: z.object({
            name: z.string(),
            displacement: z.nullable(imageLike),
            displacementMax: numberLike,
            displacementMin: numberLike,
            displacementNormalStrength: numberLike,
            displacementNormalSmoothness: numberLike,
            resolution: numberLike,
            width: numberLike,
            height: numberLike,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class ImageDisplacementToNormal extends ImageNode<ImageDisplacementToNormalParameters> {
    public override get definition() {
        return imageDisplacementToNormalDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<ImageDisplacementToNormalParameters> {
        if (this.parameters.displacement === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<ImageGenerator | null> {
        const {templateContext} = evaluator
        const {sceneManager} = templateContext

        const {displacement: displacementParameter, ...restParameters} = this.parameters

        const [displacementValid, displacementInvalid] = scope.branch(evaluator.evaluate(displacementParameter))
        const [displacementMaxValid, displacementMaxInvalid] = scope.branch(evaluator.evaluate(restParameters.displacementMax))
        const [displacementMinValid, displacementMinInvalid] = scope.branch(evaluator.evaluate(restParameters.displacementMin))
        const [displacementNormalStrengthValid, displacementNormalStrengthInvalid] = scope.branch(evaluator.evaluate(restParameters.displacementNormalStrength))
        const [displacementNormalSmoothnessValid, displacementNormalSmoothnessInvalid] = scope.branch(
            evaluator.evaluate(restParameters.displacementNormalSmoothness),
        )
        const [resolutionValid, resolutionInvalid] = scope.branch(evaluator.evaluate(restParameters.resolution))
        const [widthValid, widthInvalid] = scope.branch(evaluator.evaluate(restParameters.width))
        const [heightValid, heightInvalid] = scope.branch(evaluator.evaluate(restParameters.height))

        const displacementNode = scope.pureLambda(
            scope.tuple(displacementValid, widthValid, heightValid),
            ([displacement, width, height]) => ({
                node: displacement.imageNode.generator(getImageGeneratorDataForWidthAndHeight(width, height)).color,
                contentHash: displacement.imageNode.hash,
            }),
            "displacementNode",
        )
        const displacementTextureSizePx = scope.pureLambda(
            scope.tuple(widthValid, heightValid, resolutionValid),
            ([width, height, resolution]) => {
                return {width: Math.ceil(width * resolution), height: Math.ceil(height * resolution)}
            },
            "computeTextureSizePx",
        )
        const displacementTextureWidthPx = scope.get(displacementTextureSizePx, "width")
        const displacementTextureHeightPx = scope.get(displacementTextureSizePx, "height")

        const [displacementTextureNodeValid, displacementTextureNodeInvalid] = scope.branch(
            scope.pureLambda<HashedMaterialNodeWithColorOutput, HashedMaterialNodeWithColorOutput | null>(
                displacementNode,
                (displacementNode) => {
                    if (!(displacementNode?.node instanceof MaterialConnection)) throw new Error("Expected displacement node to be a wrapped node")
                    return displacementNode.node.destination instanceof TexImage ? displacementNode : null
                },
                "displacementTextureNode",
            ),
        )

        const {bakedTextureData: bakedDisplacementTexture} = scope.node(BakeTexture, {
            sceneManager,
            textureNode: scope.pureLambda(
                scope.tuple(displacementTextureNodeInvalid, displacementNode),
                ([_, displacementNode]) => displacementNode,
                "bakedDisplacementTextureNodeInput",
            ),
            widthUV: widthValid,
            heightUV: heightValid,
            widthPx: displacementTextureWidthPx,
            heightPx: displacementTextureHeightPx,
        })

        const bakedDisplacementTextureNode = scope.pureLambda(
            scope.tuple(scope.value(bakedDisplacementTexture, bakedTextureDataDesc, "bakedDisplacementTexture"), widthValid, heightValid),
            ([bakedDisplacementTexture, width, height]) => {
                const imageNode = generateImageNodeForInMemoryImageData(
                    bakedDisplacementTexture.imageData,
                    getImageGeneratorDataForWidthAndHeight(width, height),
                )
                return {
                    node: imageNode.color,
                    contentHash: hashObject({
                        type: "BakedDisplacementTexture",
                        sourceHash: bakedDisplacementTexture.contentHash,
                        width,
                        height,
                    }),
                }
            },
            "bakedDisplacementTextureNode",
        )

        const normalTextureNode = scope.pureLambda<[HashedMaterialNodeWithColorOutput, number, number, number, number], HashedMaterialNodeWithColorOutput>(
            scope.tuple(
                scope.phi(displacementTextureNodeValid, bakedDisplacementTextureNode),
                displacementMaxValid,
                displacementMinValid,
                displacementNormalStrengthValid,
                displacementNormalSmoothnessValid,
            ),
            ([displacementTextureNode, displacementMax, displacementMin, displacementNormalStrength, displacementNormalSmoothness]) => {
                if (!(displacementTextureNode.node instanceof MaterialConnection)) {
                    throw new Error("Expected displacement texture node to be a wrapped node")
                }
                return {
                    node: materialConnection(
                        new HeightToNormal({
                            parameters: {
                                inputs: {
                                    color: displacementTextureNode.node,
                                },
                                settings: {
                                    displacementMax: displacementMax ?? 1,
                                    displacementMin: displacementMin ?? 0,
                                    displacementNormalStrength: displacementNormalStrength ?? 1,
                                    displacementNormalSmoothness: displacementNormalSmoothness ?? 1,
                                },
                            },
                        }),
                        "color",
                    ),
                    contentHash: hashObject({
                        type: "ImageDisplacementToNormal",
                        displacementTextureNode: displacementTextureNode.contentHash,
                        displacementMax,
                        displacementMin,
                        displacementNormalStrength,
                        displacementNormalSmoothness,
                    }),
                }
            },
            "normalTextureNode",
        )

        const {bakedTextureData: bakedNormalTexture} = scope.node(BakeTexture, {
            sceneManager,
            textureNode: normalTextureNode,
            widthUV: widthValid,
            heightUV: heightValid,
            widthPx: displacementTextureWidthPx,
            heightPx: displacementTextureHeightPx,
        })

        const result = scope.pureLambda(
            scope.value(bakedNormalTexture, bakedTextureDataDesc, "bakedNormalTexture"),
            (bakedNormalTexture) => {
                return {
                    imageNode: {
                        generator: (generatorData: ImageNodeGeneratorData) =>
                            generateImageNodeForInMemoryImageData(bakedNormalTexture.imageData, generatorData),
                        hash: bakedNormalTexture.contentHash,
                    },
                }
            },
            "imageDisplacementToNormalImageGenerator",
        )

        return scope.phi(
            result,
            displacementInvalid,
            displacementMaxInvalid,
            displacementMinInvalid,
            displacementNormalSmoothnessInvalid,
            displacementNormalStrengthInvalid,
            resolutionInvalid,
            widthInvalid,
            heightInvalid,
        )
    }
}
